import { useState, useEffect, useCallback } from "react";
import {
  BarChart3, Users, BookOpen, Shield, CheckCircle2, Clock,
  AlertTriangle, Target, Activity, TrendingUp, Filter,
  ChevronRight, Eye, Sparkles, Loader2, Calendar, Building2,
  ArrowUpRight, ArrowDownRight, GraduationCap,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart as RPieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, FunnelChart, Funnel, LabelList,
} from "recharts";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const COLORS = ["#22c55e", "#3b82f6", "#a855f7", "#f59e0b", "#ec4899", "#06b6d4", "#ef4444", "#84cc16"];
const PERIODS = [
  { id: "last_30", label: "Last 30 Days" },
  { id: "this_quarter", label: "This Quarter" },
  { id: "this_year", label: "This Year" },
  { id: "last_90", label: "Last 90 Days" },
];

const KPI = ({ label, value, sub, icon: Icon, color, trend, trendDir }) => (
  <Card className="border shadow-none" data-testid={`analytics-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="w-4 h-4" />
        </div>
        {trend && (
          <span className={cn("text-[10px] font-medium flex items-center gap-0.5", trendDir === "up" ? "text-green-600" : "text-red-500")}>
            {trendDir === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{trend}
          </span>
        )}
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      {sub && <p className="text-[9px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("all");
  const [period, setPeriod] = useState("this_quarter");
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (user?.role === "super_admin") {
      api.get("/analytics/tenants").then(r => setTenants(r.data || [])).catch(() => {});
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (tenantId !== "all") params.set("tenant_id", tenantId);
      const res = await api.get(`/analytics/enhanced?${params}`);
      setData(res.data);
    } catch { toast.error("Failed to load analytics"); }
    setLoading(false);
  }, [period, tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchAI = async (section) => {
    if (!data) return;
    setAiLoading(true);
    try {
      const res = await api.post("/analytics/ai-insight", { section, metrics: data.kpis });
      setAiInsight(res.data.insight);
    } catch { toast.error("AI insight failed"); }
    setAiLoading(false);
  };

  if (loading) return (
    <AdminLayout title="Analytics">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  const k = data?.kpis || {};

  return (
    <AdminLayout title="Analytics">
      <div className="space-y-5 max-w-[1400px]" data-testid="analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold">Analytics</h1>
            <p className="text-sm text-muted-foreground">Enterprise learning intelligence & performance insights</p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "super_admin" && (
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger className="h-8 w-48 text-xs" data-testid="tenant-filter">
                  <Building2 className="w-3 h-3 mr-1" /><SelectValue placeholder="All Tenants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-1">
              {PERIODS.map(p => (
                <Button key={p.id} size="sm" variant={period === p.id ? "default" : "outline"} className="h-7 text-[11px]" onClick={() => setPeriod(p.id)} data-testid={`period-${p.id}`}>
                  {p.label}
                </Button>
              ))}
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => fetchAI("overview")} disabled={aiLoading} data-testid="ai-insight-btn">
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Insights
            </Button>
          </div>
        </div>

        {/* AI Insight */}
        {aiInsight && (
          <Card className="border-2 border-violet-200 bg-violet-50/30 shadow-none" data-testid="ai-insight-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /><div className="text-sm whitespace-pre-line">{aiInsight}</div></div>
            </CardContent>
          </Card>
        )}

        {/* KPI Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KPI label="Total Learners" value={k.total_learners?.toLocaleString()} icon={Users} color="text-blue-600 bg-blue-50" sub={`${k.active_users} active`} />
          <KPI label="DAU / MAU" value={`${k.dau} / ${k.mau}`} icon={Activity} color="text-green-600 bg-green-50" sub={`WAU: ${k.wau}`} trend={k.mau > 0 ? `${Math.round(k.dau / Math.max(k.mau, 1) * 100)}%` : "0%"} trendDir="up" />
          <KPI label="Total Enrollments" value={k.total_enrollments?.toLocaleString()} icon={BookOpen} color="text-violet-600 bg-violet-50" sub={`${k.period_enrollments} this period`} />
          <KPI label="Completion Rate" value={`${k.completion_rate}%`} icon={CheckCircle2} color="text-emerald-600 bg-emerald-50" sub={`${k.total_completions} completed`} />
          <KPI label="Learning Hours" value={`${k.total_hours?.toLocaleString()}h`} icon={Clock} color="text-amber-600 bg-amber-50" sub={`Avg progress: ${k.avg_progress}%`} />
          <KPI label="Compliance" value={`${k.compliance_rate}%`} icon={Shield} color={k.compliance_overdue > 0 ? "text-red-600 bg-red-50" : "text-teal-600 bg-teal-50"} sub={k.compliance_overdue > 0 ? `${k.compliance_overdue} overdue` : "All on track"} />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Monthly Trend */}
          <Card className="border shadow-none lg:col-span-2" data-testid="monthly-trend-chart">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Monthly Trend</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data?.monthly_trend}>
                  <defs>
                    <linearGradient id="enrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="compGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} /><stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={v => v?.split(" ")[0]} />
                  <YAxis tick={{ fontSize: 10 }} width={35} />
                  <Tooltip />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  <Area type="monotone" dataKey="enrollments" stroke="#3b82f6" fill="url(#enrGrad)" strokeWidth={2} name="Enrollments" />
                  <Area type="monotone" dataKey="completions" stroke="#22c55e" fill="url(#compGrad)" strokeWidth={2} name="Completions" />
                  <Line type="monotone" dataKey="active_users" stroke="#f59e0b" strokeWidth={1.5} dot={{ r: 2 }} name="Active Users" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Completion Funnel */}
          <Card className="border shadow-none" data-testid="funnel-chart">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Completion Funnel</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data?.funnel} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="stage" type="category" tick={{ fontSize: 10 }} width={75} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={28}>
                    {data?.funnel?.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top Courses */}
          <Card className="border shadow-none" data-testid="top-courses-chart">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Top Courses by Enrollment</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data?.top_courses?.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="title" type="category" tick={{ fontSize: 9 }} width={120} />
                  <Tooltip />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="enrolled" fill="#3b82f6" radius={[0, 3, 3, 0]} barSize={14} name="Enrolled" />
                  <Bar dataKey="completed" fill="#22c55e" radius={[0, 3, 3, 0]} barSize={14} name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card className="border shadow-none" data-testid="category-chart">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Category Performance</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={260}>
                <RPieChart>
                  <Pie data={data?.category_breakdown?.slice(0, 7)} dataKey="enrolled" nameKey="category" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                    {data?.category_breakdown?.slice(0, 7).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, name, props) => [`${v} enrolled (${props.payload.rate}% complete)`, props.payload.category]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 9 }} />
                </RPieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Score Distribution + Department */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border shadow-none" data-testid="score-distribution">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Assessment Score Distribution</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data?.score_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="range" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={30} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={36} name="Students">
                    {data?.score_distribution?.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Department Breakdown */}
          <Card className="border shadow-none" data-testid="department-breakdown">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Department Performance</CardTitle></CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    {["Department", "Users", "Enrolled", "Completed", "Rate", "Avg Score", "Hours"].map(h => (
                      <th key={h} className="p-1.5 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {data?.department_breakdown?.map(d => (
                      <tr key={d.department} className="border-b hover:bg-muted/20">
                        <td className="p-1.5 text-xs font-medium">{d.department}</td>
                        <td className="p-1.5 text-xs">{d.users}</td>
                        <td className="p-1.5 text-xs">{d.enrolled}</td>
                        <td className="p-1.5 text-xs">{d.completed}</td>
                        <td className="p-1.5"><Badge className={cn("text-[9px]", d.completion_rate >= 70 ? "bg-green-50 text-green-700" : d.completion_rate >= 40 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700")}>{d.completion_rate}%</Badge></td>
                        <td className="p-1.5 text-xs">{d.avg_score}%</td>
                        <td className="p-1.5 text-xs">{d.hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tenant Breakdown (Super Admin) */}
        {data?.tenant_breakdown?.length > 0 && (
          <Card className="border shadow-none" data-testid="tenant-breakdown">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><Building2 className="w-4 h-4" /> Analytics by Tenant</CardTitle></CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    {["Tenant", "Learners", "Enrollments", "Completions", "Rate", "Compliance", "Avg Score", "Hours"].map(h => (
                      <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {data.tenant_breakdown.map(t => (
                      <tr key={t.tenant_id} className="border-b hover:bg-muted/20">
                        <td className="p-2"><span className="text-sm font-medium">{t.tenant_name}</span></td>
                        <td className="p-2 text-sm">{t.users}</td>
                        <td className="p-2 text-sm">{t.enrollments}</td>
                        <td className="p-2 text-sm">{t.completions}</td>
                        <td className="p-2"><Badge className={cn("text-[9px]", t.completion_rate >= 70 ? "bg-green-50 text-green-700" : t.completion_rate >= 40 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700")}>{t.completion_rate}%</Badge></td>
                        <td className="p-2"><Badge className={cn("text-[9px]", t.compliance_rate >= 80 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700")}>{t.compliance_rate}%</Badge></td>
                        <td className="p-2 text-sm">{t.avg_score}%</td>
                        <td className="p-2 text-sm">{t.learning_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AnalyticsPage;
